import { useEffect, useMemo, useRef, useState } from 'react';
import debug from 'debug';
import { HostedApp } from '@components/hosted-app/HostedApp';
import { environment } from '@environment';
import { usePassthroughParams } from '@hooks/usePassthroughParams';
import { useParams } from 'react-router-dom';
import { useAppUrl } from '@hooks/useAppUrl';
import { Page } from '../Page';
import templateAliases from '@assets/data/templatealias.json';
import './CreatePage.scss';
import { ParamName } from '@common/ParamName';
import { paramsToSearchParams } from '@hooks/useParamsToSearchParams';
import { mainSuite } from '@services/ServiceFactory';
import { SvsNavbarEvent } from '@play-co/svs-navbar';
import VideoPlayer from '@components/VideoPlayer';

import loadingVideoSrc from '@assets/create/OpeningVideoCreate_1mbs.mp4';
import loadingVideoPoster from '@assets/create/OpeningVideoCreate_1mbs.jpg';
import { useClassName } from '../../common/UseClassName';
import { useAppState } from '../../context/AppContext';
import { SearchParam } from '../../common/SearchParam';

const log = debug('app:pages:create');

const predefinedSearchParams = [
  { key: 'iframed', value: '1' },
];

// page param to search param mapping for template links
const templateMapping = {
  [ParamName.Template]: ParamName.Template,
};

// page param to search param mapping for story links
const storyMapping = {
  [ParamName.WalletAddress]: ParamName.WalletAddress,
  [ParamName.StoryIndex]: ParamName.StoryIndex,
};

// allows empty strings, since empty strings are falsy
function isStrDefined(str: string | null) {
  return typeof str === 'string';
}

// get relevant search params
function useStoryOrTemplateSearchParams() {
  const params = useParams();
  const paramItems = useMemo(() => {
    // if one param, it's a template link
    if (params[ParamName.Template]) {
      // convert page params to search params
      const items = paramsToSearchParams(params, templateMapping)
        .map(({ key, value }) => {
          // ignore non-template key
          if (key !== ParamName.Template) {
            return { key, value };
          }
          // replace known aliases
          return { key, value: templateAliases[value] ?? value };
        });
      return [
        ...items,
        ...predefinedSearchParams,
      ];
    }

    // if two params, it's a story link
    if (params[ParamName.WalletAddress] && params[ParamName.StoryIndex]) {
      // convert page params to search params
      const items = paramsToSearchParams(params, storyMapping);
      return [
        ...items,
        ...predefinedSearchParams,
      ];
    }

    // if no param, just load creator normally
    return predefinedSearchParams;
  }, [params]);
  return paramItems;
}

export function CreatePage() {
  const navbarBridge = mainSuite.navbarService;
  const appState = useAppState();
  // convert page params to search params according to the number of params
  const combinedSearch = useStoryOrTemplateSearchParams();

  // combine passthrough params with page's search params
  const searchParamsStr = usePassthroughParams(environment.passthroughParams, combinedSearch);

  // appUrl override if enabled
  const url = useAppUrl(environment.createUrl);

  const [showVideoDom, setShowVideoDom] = useState(true);
  const [initted, setInitted] = useState(false);
  const [loaded, setLoaded] = useState(appState.initialSearchParams.has(SearchParam.NoVideo));
  const [videoRef, setVideoRef] = useState<HTMLVideoElement>(null);
  const overlayClassName = useClassName('loading-video-overlay', !loaded && 'show');

  useEffect(() => {
    if (initted) { return; }
    if (!appState?.initialSearchParams) { return; }

    if (appState.initialSearchParams.has(SearchParam.NoVideo)) {
      setLoaded(true);
    }
    setInitted(true);
  }, [appState, initted, setInitted]);

  useEffect(() => {
    if (loaded) {
      return;
    }
    if (appState?.initialSearchParams?.has(SearchParam.FullVideo)) {
      return;
    }
    function onHostedAppLoaded() {
      log('hosted app loaded');
      setLoaded(true);
    }

    navbarBridge.once(SvsNavbarEvent.HostedAppLoaded, onHostedAppLoaded);

    return () => {
      navbarBridge.off(SvsNavbarEvent.HostedAppLoaded, onHostedAppLoaded);
    };
  }, [navbarBridge, loaded, setLoaded, videoRef, appState]);

  useEffect(() => {
    if (!videoRef) {
      return;
    }

    function onVideoEnded() {
      log('video ended');
      setLoaded(true);
    }

    videoRef.addEventListener('ended', onVideoEnded, false);

    return () => {
      videoRef.removeEventListener('ended', onVideoEnded, false);
    }
  }, [videoRef]);

  useEffect(() => {
    if (!loaded === showVideoDom) {
      // already showing or hidden
      return;
    }

    // mismatch means it needs to transition, handled via css
    // but transition event listeners are unreliable, so we use
    // setTimeout
    const timeoutId = setTimeout(() => {
      setShowVideoDom(!loaded);
    }, 500); // matches transition duration in css

    return () => clearTimeout(timeoutId);
  }, [loaded, showVideoDom, setShowVideoDom]);

  // only allow HostedApp to be shown if url and searchParamsStr are defined
  // to prevent loading twice
  return <Page title='Creator' className='create-page' analyticsPageName='create-page-site'
    hideFooter
    fullHeight
    fixedNavbar
    hideNavbar={loaded}
    analyticsManualEntryFinal
  >
    { url && isStrDefined(searchParamsStr) && <HostedApp src={`${url}?${searchParamsStr}`} /> }
    { showVideoDom && (
      <div className={overlayClassName}>
        <VideoPlayer
          src={loadingVideoSrc}
          poster={loadingVideoPoster}
          autoPlay
          muted
          preload='auto'
          playsInline
          ref={setVideoRef}
        />
      </div>
    ) }
  </Page>;
}
